import { TABLE_SORTER_TOOLTIP } from '../../../shared/ui/list-styles.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Встречи внутри карточки сущности: список по этой сущности + назначение с
// автопривязкой. Фильтрация — на сервере (`GET /meetings?dealId=…`), поэтому
// панель не тянет лишнего.
import { useState, type ReactElement } from 'react';
import { Button, Spin, Table, Tag, Tooltip } from 'antd';
import { EmptyState } from '../../../components/entity-detail/EmptyState.js';
import type { ColumnsType } from 'antd/es/table/interface';
import { CalendarOutlined, EditOutlined } from '@ant-design/icons';

import { formatPersonName } from '../../../shared/format/index.js';
import { OverdueDate } from '../../../shared/ui/OverdueDate.js';
import { meetingIcsUrl, type MeetingItem } from '../api/meetings.client.js';
import { MeetingFormModal } from './MeetingFormModal.js';
import { useMeetings } from '../hooks/useMeetings.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';

interface EntityMeetingsPanelProps {
  dealId?: string;
  contactId?: string;
  companyId?: string;
  /** Встречи по задаче и по документу: обсуждение правок — это встреча документа. */
  taskId?: string;
  documentId?: string;
  title?: string;
}

export function EntityMeetingsPanel({
  dealId,
  contactId,
  companyId,
  taskId,
  documentId,
  title = i18n.t('crm.features.features.meetings.components.entitymeetingspanel.встречи'),
}: EntityMeetingsPanelProps): ReactElement {
  useI18nSubscription();
  const filters = {
    ...(dealId ? { dealId } : {}),
    ...(contactId ? { contactId } : {}),
    ...(companyId ? { companyId } : {}),
    ...(taskId ? { taskId } : {}),
    ...(documentId ? { documentId } : {}),
  };
  const { data, isLoading, isError, error } = useMeetings(filters);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<MeetingItem | null>(null);

  const meetings = data ?? [];

  const columns: ColumnsType<MeetingItem> = [
    {
      title: i18n.t('crm.features.features.meetings.components.entitymeetingspanel.тема'),
      key: 'title',
      render: (_, meeting) => (
        <button
          type="button"
          className="text-left font-medium text-[var(--ant-color-text)] hover:text-[var(--ant-color-primary)]"
          onClick={() => {
            setEditing(meeting);
            setFormOpen(true);
          }}
        >
          {meeting.title}
        </button>
      ),
    },
    {
      title: i18n.t('crm.features.features.meetings.components.entitymeetingspanel.начало'),
      key: 'startsAt',
      width: 170,
      sorter: (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
      defaultSortOrder: 'ascend',
      render: (_, meeting) => (
        <OverdueDate
          iso={meeting.startsAt}
          closed={meeting.status !== 'scheduled'}
          title={i18n.t(
            'crm.features.features.meetings.components.entitymeetingspanel.встреча_уже_прошла_но_не_закрыта',
          )}
        />
      ),
    },
    {
      title: i18n.t('crm.features.features.meetings.components.entitymeetingspanel.участники'),
      key: 'participants',
      render: (_, meeting) =>
        meeting.participants.length === 0 ? (
          <span className="text-[var(--ant-color-text-quaternary)]">—</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {meeting.participants.slice(0, 2).map((participant) => (
              <Tag key={participant.id}>
                {participant.member
                  ? participant.member.user.name || participant.member.user.email
                  : participant.contact
                    ? formatPersonName(participant.contact.firstName, participant.contact.lastName)
                    : (participant.guestName ??
                      participant.guestEmail ??
                      i18n.t(
                        'crm.features.features.meetings.components.entitymeetingspanel.гость',
                      ))}
              </Tag>
            ))}
            {meeting.participants.length > 2 ? (
              <Tag>
                {i18n.t('crm.features.features.meetings.components.entitymeetingspanel.еще')}
                {meeting.participants.length - 2}
              </Tag>
            ) : null}
          </div>
        ),
    },
    {
      title: i18n.t('crm.features.features.meetings.components.entitymeetingspanel.действия'),
      key: 'actions',
      width: 100,
      render: (_, meeting) => (
        <div className="flex gap-1">
          <Tooltip
            title={i18n.t(
              'crm.features.features.meetings.components.entitymeetingspanel.редактировать',
            )}
          >
            <Button
              size="small"
              type="text"
              aria-label={i18n.t(
                'crm.features.features.meetings.components.entitymeetingspanel.редактировать_встречу',
              )}
              icon={<EditOutlined />}
              onClick={() => {
                setEditing(meeting);
                setFormOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip
            title={i18n.t(
              'crm.features.features.meetings.components.entitymeetingspanel.скачать_ics',
            )}
          >
            <Button
              size="small"
              type="text"
              aria-label={i18n.t(
                'crm.features.features.meetings.components.entitymeetingspanel.скачать_приглашение',
              )}
              icon={<CalendarOutlined />}
              href={meetingIcsUrl(meeting.id)}
              target="_blank"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="px-6 py-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-[0.07em] text-[var(--ant-color-text-secondary)]">
          {title}
        </div>
        <Button
          type="primary"
          size="small"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          {i18n.t('crm.features.features.meetings.components.entitymeetingspanel.встреча')}
        </Button>
      </div>

      {isError ? (
        <ErrorState
          title={i18n.t(
            'crm.features.features.meetings.components.entitymeetingspanel.не_удалось_загрузить_встречи',
          )}
          error={error}
        />
      ) : isLoading ? (
        <div className="flex justify-center py-6">
          <Spin />
        </div>
      ) : meetings.length === 0 ? (
        <EmptyState
          text={i18n.t(
            'crm.features.features.meetings.components.entitymeetingspanel.встреч_пока_нет',
          )}
        />
      ) : (
        <Table<MeetingItem>
          showSorterTooltip={TABLE_SORTER_TOOLTIP}
          size="small"
          rowKey="id"
          dataSource={meetings}
          columns={columns}
          pagination={meetings.length > 10 ? { pageSize: 10, size: 'small' } : false}
          scroll={{ x: 'max-content' }}
        />
      )}

      {formOpen ? (
        <MeetingFormModal
          open
          onClose={() => setFormOpen(false)}
          meeting={editing}
          {...(dealId ? { lockedDealId: dealId } : {})}
          {...(contactId ? { lockedContactId: contactId } : {})}
          {...(companyId ? { lockedCompanyId: companyId } : {})}
          {...(taskId ? { lockedTaskId: taskId } : {})}
          {...(documentId ? { lockedDocumentId: documentId } : {})}
        />
      ) : null}
    </div>
  );
}
